import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom, map } from 'rxjs';

import { ContactRequestDto } from '@app/data/dtos/contact/contact.dto';
import { MessageDto } from '@app/data/dtos/message/message.dto';
import { PlatesDto } from '@app/data/dtos/personal-data/plates.dto';
import { RepresentedUserDto, UserLoginDto, UserLoginPhoneDto, UserOnBoardingDto } from '@app/data/dtos/user/user.dto';
import { mapMessageData } from '@app/data/mappers/message/message.mapper';
import { mapLicensePlates, mapPlatesToDto } from '@app/data/mappers/personal-data/personal-data.mappers';
import {
  mapRepresentedUserData,
  mapUserLogin,
  mapUserLoginPhone,
  mapUserOnBoarding
} from '@app/data/mappers/user/user.mapper';
import { UserRepository } from '@app/domain/repositories/user/user.repository.contract';
import { ENCTYPE } from '@app/entities/constants/enctype/enctype.constants';
import { TourKeys } from '@app/entities/enums/tour.enum';
import { NewsItem } from '@app/entities/interfaces/news/news.interface';
import { Message } from '@app/entities/models/message/message.model';
import { Plate } from '@app/entities/models/personal-data/plates.model';
import { UserLogin, UserLoginPhone } from '@app/entities/models/user/login.model';
import { UserOnBoarding } from '@app/entities/models/user/on-boarding.model';
import { RepresentedUser, User } from '@app/entities/models/user/user.model';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

@Injectable({
  providedIn: 'root'
})
export class UserRepositoryImpl extends MRepository implements UserRepository {
  url = '/user';

  constructor() {
    super(`${ENV.API_URL}`);
  }

  requestUserInfo(): Promise<User> {
    const httpOptions = {
      headers: new HttpHeaders({
        'content-language': navigator.language.substring(0, 2)
      })
    };

    return lastValueFrom(this.post<User>(`${this.url}/info`, httpOptions));
  }

  syncImage(): Promise<User> {
    return lastValueFrom(this.get(`${this.url}/image`, {}));
  }

  getRepresentedUsers(): Observable<RepresentedUser[]> {
    return this.get<RepresentedUserDto[]>(`${this.url}/represented-users`, {
      context: new HttpContext().set(SECTION_KEY, 'represented-users')
    }).pipe(map(mapRepresentedUserData));
  }

  getContactMessages(): Observable<Message[]> {
    return this.get<MessageDto[]>(`/contact`).pipe(map(mapMessageData));
  }

  setAcceptConditions(email: string, acceptation: boolean): Observable<object> {
    const request = {
      body: {
        email,
        acceptation
      }
    };

    return this.post(`${this.url}/legal-conditions`, request);
  }

  editAvatar(newAvatar: File): Observable<object> {
    const formData = new FormData();
    formData.append('image', newAvatar);
    formData.append('mime_type', newAvatar.type);
    const httpOptions = {
      headers: new HttpHeaders({
        enctype: ENCTYPE.BOUNDARY
      })
    };

    const request = {
      body: formData,
      headers: httpOptions.headers
    };

    return this.post(`${this.url}/image`, request);
  }

  deleteAvatar(): Observable<object> {
    return this.delete(`${this.url}/image`);
  }

  async setLanguage(language_code: string, language_country: string): Promise<User> {
    const request = {
      body: {
        language_code,
        language_country
      }
    };

    return lastValueFrom(this.post(`${this.url}/language`, request));
  }

  postContact(data: ContactRequestDto): Observable<object> {
    const request = {
      body: data
    };

    return this.post(`/contact`, request);
  }

  setAcceptNews(newsItem: NewsItem): Observable<object> {
    newsItem.accepted = true;
    const request = {
      body: newsItem
    };

    return this.post(`${this.url}/news`, request);
  }

  setWalkthroughSeen(walkthroughKey: TourKeys): Observable<object> {
    const request = {
      body: { key: walkthroughKey }
    };

    return this.post(`${this.url}/walkthrough`, request);
  }

  getOnBoarding(): Observable<UserOnBoarding> {
    return this.get<UserOnBoardingDto>(`${this.url}/onboarding`).pipe(map(mapUserOnBoarding));
  }

  updateOnBoarding(): Observable<object> {
    return this.post(`${this.url}/onboarding`, {});
  }

  getUserLogin(userId: string): Observable<UserLogin> {
    let params = new HttpParams();
    params = params.append('user_id', userId);

    return this.get<UserLoginDto>(`${this.url}/v2/login-type`, { params }).pipe(map(mapUserLogin));
  }

  checkUserLoginPhone(userId: string, phone: string): Observable<UserLoginPhone> {
    const request = {
      body: { user_id: userId, phone }
    };

    return this.post<UserLoginPhoneDto>(`${this.url}/v2/phone/check`, request).pipe(map(mapUserLoginPhone));
  }

  getLicensePlates(): Observable<Plate[]> {
    return this.get<PlatesDto>(`${this.url}/license-plates`, {
      context: new HttpContext().set(SECTION_KEY, 'license-plates')
    }).pipe(map(mapLicensePlates));
  }

  postLicensePlates(data: Plate[]): Observable<void> {
    const mapPlates = mapPlatesToDto(data);
    const request = { body: mapPlates };
    return this.post(`${this.url}/license-plates`, request);
  }

  deleteLicensePlate(plate: string): Observable<void> {
    return this.delete(`${this.url}/license-plates/${plate}`);
  }
}
